import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const RealEstateApi: React.FC = () => {
  const [selectedApi, setSelectedApi] = useState('price');

  const apis = [
    { id: 'price', name: '시세 정보 API', endpoint: '/api/v1/realestate/price' },
    { id: 'transaction', name: '실거래가 API', endpoint: '/api/v1/realestate/transaction' },
    { id: 'rights', name: '권리분석 API', endpoint: '/api/v1/realestate/rights' },
    { id: 'investment', name: '투자지표 API', endpoint: '/api/v1/realestate/investment' },
  ];

  return (
    <div className="bg-gradient-to-br from-gray-50 to-primary-50/30 min-h-screen">
      {/* 헤더 */}
      <div className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-purple-600 text-white py-16 overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-primary-900/20 to-transparent"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-block mb-4">
            <span className="bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium border border-white/20">
              🏢 Real Estate API
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-white to-primary-100">
            부동산 데이터 API
          </h1>
          <p className="text-xl text-primary-50 max-w-3xl">
            실거래가, 시세 정보, 권리분석, 투자지표 등 부동산 관련 모든 데이터를 제공합니다
          </p>
        </div>
      </div>

      {/* 메인 콘텐츠 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* 사이드바 */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-card p-6 sticky top-24 border border-gray-100">
              <h3 className="font-bold text-gray-900 mb-6 text-lg flex items-center gap-2">
                <svg className="w-3.5 h-3.5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                API 목록
              </h3>
              <nav className="space-y-2">
                {apis.map((api) => (
                  <button
                    key={api.id}
                    onClick={() => setSelectedApi(api.id)}
                    className={`group w-full text-left px-4 py-3 rounded-xl transition-all duration-200 font-medium ${
                      selectedApi === api.id
                        ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-lg shadow-primary-500/30'
                        : 'text-gray-700 hover:bg-primary-50 hover:text-primary-700'
                    }`}
                  >
                    <span className="flex items-center justify-between">
                      {api.name}
                      {selectedApi === api.id && (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}
                    </span>
                  </button>
                ))}
              </nav>
              <div className="mt-6 pt-6 border-t border-gray-200">
                <Link
                  to="/developer-tools/api-test"
                  className="group block w-full bg-gradient-to-r from-success-600 to-success-700 text-white text-center px-4 py-3 rounded-xl hover:shadow-lg hover:shadow-success-500/30 transition-all duration-300 font-medium hover:-translate-y-0.5"
                >
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    API 테스트
                  </span>
                </Link>
              </div>
            </div>
          </div>

          {/* API 상세 정보 */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-card p-8 border border-gray-100 animate-fade-in">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-primary-100 rounded-xl">
                  <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-primary-700 bg-clip-text text-transparent">
                  {apis.find((api) => api.id === selectedApi)?.name}
                </h2>
              </div>

              {/* 엔드포인트 */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <svg className="w-3.5 h-3.5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  엔드포인트
                </h3>
                <div className="relative group">
                  <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-green-400 p-5 rounded-xl font-mono text-sm shadow-lg border border-gray-700">
                    <span className="inline-block px-3 py-1 bg-yellow-500/20 text-yellow-300 rounded-md font-bold mr-3">GET</span>
                    <span className="text-gray-100">{apis.find((api) => api.id === selectedApi)?.endpoint}</span>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-r from-primary-500/20 to-purple-500/20 rounded-xl blur-xl group-hover:blur-2xl transition-all duration-300 -z-10"></div>
                </div>
              </div>

              {/* 요청 헤더 */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <svg className="w-3.5 h-3.5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  요청 헤더
                </h3>
                <div className="bg-gradient-to-br from-gray-50 to-primary-50/30 p-6 rounded-xl border border-gray-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-primary-200">
                        <th className="text-left py-3 font-bold text-gray-900">헤더</th>
                        <th className="text-left py-3 font-bold text-gray-900">값</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-200 hover:bg-white/50 transition-colors">
                        <td className="py-3 font-mono text-primary-700 font-medium">Authorization</td>
                        <td className="py-3 font-mono text-gray-600">Bearer <span className="text-secondary-600">{'{access_token}'}</span></td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-white/50 transition-colors">
                        <td className="py-3 font-mono text-primary-700 font-medium">Content-Type</td>
                        <td className="py-3 font-mono text-gray-600">application/json</td>
                      </tr>
                      <tr className="hover:bg-white/50 transition-colors">
                        <td className="py-3 font-mono text-primary-700 font-medium">X-API-Key</td>
                        <td className="py-3 font-mono text-gray-600"><span className="text-secondary-600">{'{api_key}'}</span></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 요청 파라미터 */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <svg className="w-3.5 h-3.5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                  요청 파라미터
                </h3>
                <div className="bg-gradient-to-br from-gray-50 to-primary-50/30 p-6 rounded-xl border border-gray-200 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-primary-200">
                        <th className="text-left py-3 font-bold text-gray-900">파라미터</th>
                        <th className="text-left py-3 font-bold text-gray-900">타입</th>
                        <th className="text-left py-3 font-bold text-gray-900">필수</th>
                        <th className="text-left py-3 font-bold text-gray-900">설명</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-200 hover:bg-white/50 transition-colors">
                        <td className="py-3 font-mono text-primary-700 font-medium">address</td>
                        <td className="py-3 font-mono text-secondary-600">string</td>
                        <td className="py-3">
                          <span className="bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-sm">필수</span>
                        </td>
                        <td className="py-3 text-gray-700">조회할 부동산 주소</td>
                      </tr>
                      <tr className="border-b border-gray-200 hover:bg-white/50 transition-colors">
                        <td className="py-3 font-mono text-primary-700 font-medium">property_type</td>
                        <td className="py-3 font-mono text-secondary-600">string</td>
                        <td className="py-3">
                          <span className="bg-gray-200 text-gray-700 px-3 py-1 rounded-full text-xs font-medium">선택</span>
                        </td>
                        <td className="py-3 text-gray-700">부동산 유형 (아파트, 빌라, 오피스텔 등)</td>
                      </tr>
                      <tr className="hover:bg-white/50 transition-colors">
                        <td className="py-3 font-mono text-primary-700 font-medium">date_range</td>
                        <td className="py-3 font-mono text-secondary-600">string</td>
                        <td className="py-3">
                          <span className="bg-gray-200 text-gray-700 px-3 py-1 rounded-full text-xs font-medium">선택</span>
                        </td>
                        <td className="py-3 text-gray-700">조회 기간 (YYYY-MM-DD ~ YYYY-MM-DD)</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 응답 예시 */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <svg className="w-3.5 h-3.5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  응답 예시
                </h3>
                <div className="relative group">
                  <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-gray-100 p-6 rounded-xl font-mono text-sm overflow-x-auto shadow-xl border border-gray-700">
                    <pre>{`{
  "success": true,
  "data": {
    "address": "서울특별시 강남구 역삼동 123-45",
    "property_type": "아파트",
    "current_price": 1850000000,
    "predicted_price": 1920000000,
    "price_change_rate": 3.8,
    "trade_volume": 15,
    "last_updated": "2025-10-29T10:30:00Z"
  },
  "meta": {
    "request_id": "req_abc123",
    "timestamp": "2025-10-29T10:30:00Z"
  }
}`}</pre>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-r from-success-500/10 to-primary-500/10 rounded-xl blur-xl -z-10"></div>
                </div>
              </div>

              {/* 에러 코드 */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <svg className="w-3.5 h-3.5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  에러 코드
                </h3>
                <div className="bg-gradient-to-br from-gray-50 to-red-50/30 p-6 rounded-xl border border-red-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-red-200">
                        <th className="text-left py-3 font-bold text-gray-900">코드</th>
                        <th className="text-left py-3 font-bold text-gray-900">메시지</th>
                        <th className="text-left py-3 font-bold text-gray-900">설명</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-red-100 hover:bg-white/50 transition-colors">
                        <td className="py-3 font-mono text-red-600 font-bold">400</td>
                        <td className="py-3 font-medium">Bad Request</td>
                        <td className="py-3 text-gray-700">잘못된 요청 파라미터</td>
                      </tr>
                      <tr className="border-b border-red-100 hover:bg-white/50 transition-colors">
                        <td className="py-3 font-mono text-red-600 font-bold">401</td>
                        <td className="py-3 font-medium">Unauthorized</td>
                        <td className="py-3 text-gray-700">인증 실패</td>
                      </tr>
                      <tr className="border-b border-red-100 hover:bg-white/50 transition-colors">
                        <td className="py-3 font-mono text-red-600 font-bold">404</td>
                        <td className="py-3 font-medium">Not Found</td>
                        <td className="py-3 text-gray-700">데이터를 찾을 수 없음</td>
                      </tr>
                      <tr className="hover:bg-white/50 transition-colors">
                        <td className="py-3 font-mono text-red-600 font-bold">429</td>
                        <td className="py-3 font-medium">Too Many Requests</td>
                        <td className="py-3 text-gray-700">API 호출 한도 초과</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 코드 샘플 */}
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <svg className="w-3.5 h-3.5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                  코드 샘플
                </h3>
                <div className="space-y-6">
                  {/* Python */}
                  <div className="relative group">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-bold text-gray-900 flex items-center gap-2">
                        <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
                        Python
                      </h4>
                      <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">requests</span>
                    </div>
                    <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-gray-100 p-5 rounded-xl font-mono text-sm overflow-x-auto shadow-xl border border-gray-700">
                      <pre>{`import requests

url = "https://api.dataapi.com/v1/realestate/price"
headers = {
    "Authorization": "Bearer YOUR_ACCESS_TOKEN",
    "X-API-Key": "YOUR_API_KEY"
}
params = {
    "address": "서울특별시 강남구 역삼동 123-45",
    "property_type": "아파트"
}

response = requests.get(url, headers=headers, params=params)
data = response.json()
print(data)`}</pre>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl blur-xl -z-10"></div>
                  </div>

                  {/* JavaScript */}
                  <div className="relative group">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-bold text-gray-900 flex items-center gap-2">
                        <span className="w-3 h-3 bg-yellow-500 rounded-full"></span>
                        JavaScript
                      </h4>
                      <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">axios</span>
                    </div>
                    <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-gray-100 p-5 rounded-xl font-mono text-sm overflow-x-auto shadow-xl border border-gray-700">
                      <pre>{`const axios = require('axios');

const url = 'https://api.dataapi.com/v1/realestate/price';
const headers = {
  'Authorization': 'Bearer YOUR_ACCESS_TOKEN',
  'X-API-Key': 'YOUR_API_KEY'
};
const params = {
  address: '서울특별시 강남구 역삼동 123-45',
  property_type: '아파트'
};

axios.get(url, { headers, params })
  .then(response => console.log(response.data))
  .catch(error => console.error(error));`}</pre>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-xl blur-xl -z-10"></div>
                  </div>

                  {/* cURL */}
                  <div className="relative group">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-bold text-gray-900 flex items-center gap-2">
                        <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                        cURL
                      </h4>
                      <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">terminal</span>
                    </div>
                    <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-gray-100 p-5 rounded-xl font-mono text-sm overflow-x-auto shadow-xl border border-gray-700">
                      <pre>{`curl -X GET "https://api.dataapi.com/v1/realestate/price?address=서울특별시 강남구 역삼동 123-45&property_type=아파트" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -H "X-API-Key: YOUR_API_KEY"`}</pre>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-teal-500/10 rounded-xl blur-xl -z-10"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RealEstateApi;
